export interface BreadcrumbItem {
  href: string;
  label: string;
}

/**
 * More type-safe version of MakeBreadcrumbsOptions that ensures
 * the keys in items are strictly the keys from the params object.
 */
export interface MakeBreadcrumbsOptions<P extends Record<string, string | undefined>> {
  params: P;
  items: Array<{
    key: keyof P;
    label: string;
  }>;
  basePath: string;
}

/**
 * Creates breadcrumb links from query parameters
 * Each breadcrumb href includes all previous query parameters
 * The function returns key and value but NO labels - those should be handled by the caller
 * e.g )
 * params: { prefCode: '13', jobCategory: '1', jobBenefit: '1' }, items: [{ key: 'prefCode', label: '東京都' }, { key: 'jobCategory', label: 'ITエンジニア' }, { key: 'jobBenefit', label: 'フルタイム' }], basePath: '/jobs'
 * returns [{ href: '/jobs?prefCode=13', key: 'prefCode' }, { href: '/jobs?prefCode=13&jobCategory=1', key: 'jobCategory' }, { href: '/jobs?prefCode=13&jobCategory=1&jobBenefit=1', key: 'jobBenefit' }]
 */
export function makeBreadcrumbs<P extends Record<string, string | undefined>>(
  options: MakeBreadcrumbsOptions<P>
): BreadcrumbItem[] {
  const { params, items, basePath = '/jobs' } = options;

  const breadcrumbs: BreadcrumbItem[] = [];

  const queryParams: Record<string, string> = {};

  for (const { key, label } of items) {
    const value = params[key];

    if (!value) continue;

    queryParams[key as string] = value;

    const queryString = Object.entries(queryParams)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');

    breadcrumbs.push({
      href: `${basePath}?${queryString}`,
      label,
    });
  }

  return breadcrumbs;
}
